'use client';

import { faArrowUp, faArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useState } from 'react';

import { LineupPlayerSelect } from '@/components/atoms/scoreCards/LineupPlayerSelect';
import { ActionButtons } from '@/components/molecules';
import { RotationPlayerList } from '@/components/molecules/tie-breaker/RotationPlayerList';
import { LineupPlayerOption } from '@/entities';
import { PermissionRole } from '@/entities/PermissionRoles';
import { RotationPlayerInfo } from '@/entities/rotations/RotationsInfo';
import { useTeamEditParams } from '@/hooks/useTeamEditParams';
import { isPlayerDisabled, MoveDirection, PlayerGender, reorderArrayItem } from '@/infrastructure/utils/tieBreakerHelpers';
import { usePermissionStore } from '@/store/permission-store';
import { isPlayerIneligible } from '@/utils/helpers';
import { TieBreakerPair } from '@/utils/types';

export enum TieBreakerStep {
	SELECT = 'select',
	ORDER = 'order',
	SAVE = 'save'
}

interface TieBreakerFormDoublesProps {
	players: LineupPlayerOption[];
	groupTitle: string;
	onCloseStep: () => void;
	onSave?: (
		selectedPairs: TieBreakerPair[],
		team: number,
		deletedRotations?: { rotationUuid: string; playerOneUuid: string; playerTwoUuid?: string | null }[]
	) => void;
	teamTitle: string;
	isAwayTeam: boolean;
	rotationsInfo?: RotationPlayerInfo[];
	isCreatingTeamRotation: boolean;
	tieBreakerMatchUuid: string;
	canCaptainEditLineups: boolean;
	// Number of lines the division's rotation settings call for. Falls back to the roster-derived
	// count when the settings don't specify one.
	lineCount?: number;
}

export const TieBreakerFormDoubles = ({
	players,
	groupTitle,
	onCloseStep,
	onSave,
	teamTitle,
	isAwayTeam,
	rotationsInfo,
	isCreatingTeamRotation,
	tieBreakerMatchUuid,
	canCaptainEditLineups,
	lineCount
}: TieBreakerFormDoublesProps) => {
	const [step, setStep] = useState<TieBreakerStep>(TieBreakerStep.SELECT);
	const [selectedPairs, setSelectedPairs] = useState<TieBreakerPair[]>([]);
	const [_, setInitialRotatedPairs] = useState<TieBreakerPair[]>([]);

	const permissionsStore = usePermissionStore();

	const { isEditMode } = useTeamEditParams();

	const gamedayAccessRole = permissionsStore.gamedayAccess;

	const isUserCaptain = permissionsStore.userPermission === PermissionRole.TEAM_CAPTAIN;

	const canEditLineup = gamedayAccessRole || (isUserCaptain && canCaptainEditLineups);
	const canSetLineup = gamedayAccessRole || (isUserCaptain && canCaptainEditLineups);

	const selectablePlayers = useMemo(
		() => players.filter((p) => !isPlayerDisabled(p.gender as PlayerGender, groupTitle) && !isPlayerIneligible(p)),
		[players, groupTitle]
	);
	// Players may repeat across rotations, so roster size is only a fallback — the rotation settings
	// decide how many lines are played. Never shrink below what's already saved.
	const numberOfPairs = Math.max(lineCount ?? Math.floor(selectablePlayers.length / 2), rotationsInfo?.length ?? 0);

	useEffect(() => {
		if (rotationsInfo && rotationsInfo.length > 0) return;

		const newPairs = Array.from({ length: numberOfPairs }, () => ({
			player1: '',
			player2: ''
		}));

		setSelectedPairs(newPairs);
	}, [numberOfPairs, rotationsInfo]);

	useEffect(() => {
		if (!rotationsInfo || rotationsInfo.length === 0) return;

		const mappedPairs: TieBreakerPair[] = rotationsInfo.map((rotation) => ({
			player1: rotation.playerOneUuid ?? '',
			player2: rotation.playerTwoUuid ?? ''
		}));

		const remainingSlots = numberOfPairs - mappedPairs.length;
		const emptyPairs = Array.from({ length: remainingSlots }, () => ({ player1: '', player2: '' }));

		setSelectedPairs([...mappedPairs, ...emptyPairs]);

		setInitialRotatedPairs(mappedPairs);
	}, [rotationsInfo, numberOfPairs]);

	const handleSelect = (pairIndex: number, playerKey: 'player1' | 'player2', value: string) => {
		setSelectedPairs((prev) => {
			if (!prev[pairIndex]) return prev;
			const updated = [...prev];
			const updatedPair = {
				player1: playerKey === 'player1' ? value : updated[pairIndex]!.player1,
				player2: playerKey === 'player2' ? value : updated[pairIndex]!.player2
			};
			updated[pairIndex] = updatedPair;
			return updated;
		});
	};

	const handleMove = (index: number, direction: MoveDirection) => {
		setSelectedPairs((prev) => reorderArrayItem(prev, index, direction));
	};

	const isPlayerSelectedInSamePair = (uuid: string, pair: TieBreakerPair, key: 'player1' | 'player2') => {
		if (key === 'player1') {
			return pair.player2 === uuid;
		}
		if (key === 'player2') {
			return pair.player1 === uuid;
		}
		return false;
	};

	const handleSave = () => {
		const getPairKey = (p1?: string | null, p2?: string | null) => [p1, p2].filter(Boolean).sort().join('-');

		const currentKeys = new Set(validPairs.map((p) => getPairKey(p.player1, p.player2)));

		const deletedRotations = (rotationsInfo ?? [])
			.filter((rotation) => {
				const key = getPairKey(rotation.playerOneUuid, rotation.playerTwoUuid);
				return !currentKeys.has(key);
			})
			.map((rotation) => ({
				rotationUuid: rotation.rotationUuid,
				playerOneUuid: rotation.playerOneUuid,
				playerTwoUuid: rotation.playerTwoUuid ?? null
			}));

		onSave?.(validPairs, isAwayTeam ? 2 : 1, deletedRotations);
	};

	const validPairs = useMemo(() => selectedPairs.filter((pair) => pair.player1 && pair.player2), [selectedPairs]);

	const backButtonText = step === TieBreakerStep.SELECT ? 'Cancel' : 'Back';

	if (isEditMode && !canEditLineup) {
		return 'You are not allowed to edit team’s lineup.';
	}

	if (!isEditMode && !canSetLineup) {
		return 'You are not allowed to set the team’s lineup.';
	}

	const actionButtons = (backStep: TieBreakerStep, submitButtonText: string, onSubmit: () => void, submitDisabled = false) => (
		<ActionButtons
			className="w-full pt-10"
			submitButtonClassName="w-fit"
			closeButtonClassName="!w-fit w-full"
			onClose={() => (step === TieBreakerStep.SELECT ? onCloseStep() : setStep(backStep))}
			onSubmit={onSubmit}
			submitButtonText={submitButtonText}
			closeButtonText={backButtonText}
			submitButtonDisabled={submitDisabled}
		/>
	);

	const renderTeamTitle = () => (
		<p className="text-2xl pb-4 font-medium text-blue-500">
			{teamTitle} <span className="text-lg font-medium text-gray-900">({isAwayTeam ? 'A' : 'H'})</span>
		</p>
	);

	const renderSelectedPlayers = () => {
		return (
			<RotationPlayerList
				title={renderTeamTitle()}
				rotations={rotationsInfo ?? []}
				showBothPlayers
				teamTitle={teamTitle}
				tieBreakerMatchUuid={tieBreakerMatchUuid}
			/>
		);
	};

	const renderSelectStep = () => (
		<>
			<div className="w-full min-w-0 sm:min-w-[620px] min-h-[450px] overflow-y-auto p-9 rounded-3xl border border-gray-300 bg-white">
				{renderTeamTitle()}
				<p className="font-medium text-gray-500">Select players to be partners.</p>

				{selectedPairs.map((pair, index) => (
					<div key={index} className="flex flex-col gap-4">
						<label className="text-sm font-medium py-4">Doubles {index + 1}</label>
						<div className="flex flex-col sm:flex-row gap-4">
							<LineupPlayerSelect
								players={players}
								onSelect={(val) => handleSelect(index, 'player1', val as string)}
								selectedPlayer={players.find((p) => p.userUuid === pair.player1)}
								placeholder="Select player"
								playerLabel="Player 1"
								isPlayerDisabledFn={(p) =>
									isPlayerDisabled(p.gender as PlayerGender, groupTitle) ||
									isPlayerIneligible(p) ||
									isPlayerSelectedInSamePair(p.userUuid, pair, 'player1')
								}
								allowEmptySelection
							/>
							<LineupPlayerSelect
								players={players}
								onSelect={(val) => handleSelect(index, 'player2', val as string)}
								selectedPlayer={players.find((p) => p.userUuid === pair.player2)}
								placeholder="Select player"
								playerLabel="Player 2"
								isPlayerDisabledFn={(p) =>
									isPlayerDisabled(p.gender as PlayerGender, groupTitle) ||
									isPlayerIneligible(p) ||
									isPlayerSelectedInSamePair(p.userUuid, pair, 'player2')
								}
								allowEmptySelection
							/>
						</div>
					</div>
				))}
			</div>
			{actionButtons(TieBreakerStep.SELECT, 'Next', () => setStep(TieBreakerStep.ORDER), validPairs.length === 0)}
		</>
	);

	const renderOrderStep = () => (
		<>
			<div className="w-full max-w-[420px] h-[450px] overflow-y-auto p-9 rounded-3xl border border-gray-300 bg-white">
				{renderTeamTitle()}
				<p className="font-medium text-gray-600 mb-6">You can change the order by clicking the arrows to move the pair up or down.</p>
				<div className="flex flex-col gap-4">
					{validPairs.map((pair, index) => (
						<div key={index} className="flex items-center gap-5">
							<div className="flex items-center gap-2">
								<span className="w-6 h-6 flex items-center justify-center aspect-square rounded-full border border-gray-400 text-sm">
									{index + 1}
								</span>
								<div className="flex gap-3">
									{index > 0 && (
										<button type="button" onClick={() => handleMove(index, 'up')}>
											<FontAwesomeIcon icon={faArrowUp} size="sm" />
										</button>
									)}
									{index < validPairs.length - 1 && (
										<button type="button" onClick={() => handleMove(index, 'down')}>
											<FontAwesomeIcon icon={faArrowDown} size="sm" />
										</button>
									)}
								</div>
							</div>

							<div className="flex flex-col flex-1">
								<p>{players.find((p) => p.userUuid === pair.player1)?.fullName}</p>
								<p>{players.find((p) => p.userUuid === pair.player2)?.fullName}</p>
							</div>
						</div>
					))}
				</div>
			</div>
			{actionButtons(TieBreakerStep.SELECT, 'Next', () => setStep(TieBreakerStep.SAVE))}
		</>
	);

	const renderSaveStep = () => (
		<>
			<div className="w-[420px] max-w-full h-[450px] overflow-y-auto p-9 rounded-3xl border border-gray-300 bg-white">
				{renderTeamTitle()}

				<p onClick={() => setStep(TieBreakerStep.SELECT)} className="text-darkorange-400 font-semibold cursor-pointer pb-4">
					Edit
				</p>
				<div className="flex flex-col gap-4">
					{validPairs.map((pair, index) => (
						<div key={index} className="flex items-center gap-3">
							<span className="w-6 h-6 flex items-center justify-center aspect-square rounded-full border border-gray-400 text-sm">
								{index + 1}
							</span>
							<div className="flex flex-col">
								<span>{players.find((p) => p.userUuid === pair.player1)?.fullName}</span>
								<span>{players.find((p) => p.userUuid === pair.player2)?.fullName}</span>
							</div>
						</div>
					))}
				</div>
			</div>
			{actionButtons(TieBreakerStep.ORDER, 'Save', handleSave, isCreatingTeamRotation)}
		</>
	);

	if (!isEditMode && rotationsInfo?.length !== 0) {
		return renderSelectedPlayers();
	}

	return {
		[TieBreakerStep.SELECT]: renderSelectStep,
		[TieBreakerStep.ORDER]: renderOrderStep,
		[TieBreakerStep.SAVE]: renderSaveStep
	}[step]();
};
